const api =
  process.env.NODE_ENV === "production"
    ? "https://medical-api.progress-ims.com/api"
    : "https://localhost:7175/api";
const smsapi = "https://smsapi.huestimates.net/SendSms";
const login = "https://progress-login.azersigorta.az/";
const signalapi = process.env.NODE_ENV === "production"
  ? "https://medical-api.progress-ims.com/signalr"
  : "https://localhost:7175/signalr"; 

  
const printapi = "https://print.progress-ims.com/";

/** Print iframe URLs: /print/{templateId}/{id}/inline (Progress branch). */
const POLICY_PRINT_TEMPLATE_ID = 0;
const AGENT_REQUEST_PRINT_TEMPLATE_ID = 15;

function buildInlinePrintUrl(templateId, id) {
  const token = id != null ? String(id).trim() : "";
  if (!token) return "";
  return `${printapi}print/${templateId}/${encodeURIComponent(token)}/inline`;
}

/** When policy exists: /print/0/{policyId}/inline */
function buildPolicyPrintUrl(policyId) {
  return buildInlinePrintUrl(POLICY_PRINT_TEMPLATE_ID, policyId);
}

/** When no policy yet (agent request): /print/15/{reqId}/inline */
function buildAgentRequestPrintUrl(reqId) {
  return buildInlinePrintUrl(AGENT_REQUEST_PRINT_TEMPLATE_ID, reqId);
}

/**
 * Disk layout on production server:
 *   Parent/
 *     release/     ← IIS/site path for https://progress-agent.ateshgah.com
 *     pdffiles/    ← sibling of release (OUTSIDE site root)
 *
 * Browser cannot leave the site root with "../".
 * So /pdffiles/* works only if IIS has a Virtual Directory
 *   alias "pdffiles" → physical Parent/pdffiles
 *
 * Optional override: VUE_APP_PDF_FILES_BASE=https://host/pdffiles/
 */
const PDF_FILES_FOLDER = "pdffiles";

/** Print PDF templates — same as former print service ids. */
const PDF_PRINT_TYPE_POLICY = 0;
const PDF_PRINT_TYPE_AGENT_REQUEST = 15;

/**
 * File name in pdffiles (without .pdf): `{type}_{id}`
 * e.g. 0_5805 (policy) or 15_49 (agent request).
 */
function buildPrintPdfFileName(type, id) {
  const t = type != null ? String(type).trim() : "";
  const i = id != null ? String(id).trim() : "";
  if (!t || !i) return "";
  return `${t}_${i}`;
}

/** Base URL for pdffiles/. */
function resolvePdfFilesBaseUrl() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const pathname =
    typeof window !== "undefined" && window.location?.pathname
      ? window.location.pathname
      : "/";

  const envBase = process.env.VUE_APP_PDF_FILES_BASE;
  if (envBase != null && String(envBase).trim() !== "") {
    const raw = String(envBase).trim();
    const base = raw.endsWith("/") ? raw : `${raw}/`;
    console.log("[PDF] resolvePdfFilesBaseUrl from VUE_APP_PDF_FILES_BASE", base);
    return base;
  }

  // URL still contains /release/... (rare) → sibling path in URL space.
  const releaseIdx = pathname.toLowerCase().indexOf("/release");
  if (releaseIdx >= 0) {
    const parent = pathname.slice(0, releaseIdx) || "";
    const base = `${origin}${parent}/${PDF_FILES_FOLDER}/`;
    console.log("[PDF] resolvePdfFilesBaseUrl via /release/ in URL", { pathname, base });
    return base;
  }

  // Production: site root === physical "release" folder.
  // PDF URL must be /pdffiles/... via IIS Virtual Directory to sibling folder.
  if (process.env.NODE_ENV === "production") {
    const base = `${origin}/${PDF_FILES_FOLDER}/`;
    console.log("[PDF] resolvePdfFilesBaseUrl production (site=release folder)", {
      pathname,
      origin,
      base,
      hint:
        "Site root is release/. pdffiles is sibling on disk — map IIS Virtual Directory /pdffiles → Parent/pdffiles",
    });
    return base;
  }

  // Local npm run serve: public/pdffiles → /pdffiles/
  const localBase = process.env.BASE_URL || "/";
  const prefix = localBase.endsWith("/") ? localBase : `${localBase}/`;
  const base = `${prefix}${PDF_FILES_FOLDER}/`;
  console.log("[PDF] resolvePdfFilesBaseUrl local", { pathname, base });
  return base;
}

/**
 * Resolve URL for a PDF in pdffiles/{name}.pdf
 * Example: buildPdfFileUrl("0_5805") → ".../pdffiles/0_5805.pdf"
 */
function buildPdfFileUrl(fileName) {
  const raw = fileName != null ? String(fileName).trim() : "";
  if (!raw) return "";
  const name = raw.replace(/\.pdf$/i, "");
  if (!name) return "";
  const base = resolvePdfFilesBaseUrl();
  const url = `${base}${encodeURIComponent(name)}.pdf`;
  console.log("[PDF] buildPdfFileUrl", {
    fileName: raw,
    name,
    base,
    url,
    NODE_ENV: process.env.NODE_ENV,
    BASE_URL: process.env.BASE_URL,
    href: typeof window !== "undefined" ? window.location.href : null,
    pathname: typeof window !== "undefined" ? window.location.pathname : null,
  });
  return url;
}

function isPdfContentType(contentType) {
  const t = String(contentType || "").toLowerCase();
  if (!t) return null; // unknown
  if (t.includes("pdf") || t.includes("octet-stream")) return true;
  if (t.includes("html") || t.includes("text/plain") || t.includes("json")) return false;
  return null;
}

async function looksLikePdfBlob(blob) {
  if (!blob || blob.size < 5) return false;
  const head = await blob.slice(0, 5).text();
  return head.startsWith("%PDF");
}

/** Probe whether a real PDF exists at URL (not SPA/HTML 200 fallback). */
async function probePdfFileUrl(url) {
  const started = performance.now();
  const result = {
    url,
    ok: false,
    status: null,
    statusText: "",
    contentType: "",
    contentLength: "",
    elapsedMs: 0,
    error: null,
  };
  if (!url) {
    result.error = "empty url";
    console.warn("[PDF] probe skipped — empty url");
    return result;
  }
  try {
    // Always GET a bit of the file — IIS often returns 200 text/html for missing paths.
    const response = await fetch(url, { method: "GET", cache: "no-store" });
    result.status = response.status;
    result.statusText = response.statusText || "";
    result.contentType = response.headers.get("content-type") || "";
    result.contentLength = response.headers.get("content-length") || "";

    if (!response.ok) {
      result.error = `http ${response.status}`;
      result.elapsedMs = Math.round(performance.now() - started);
      console.error("[PDF] file not found or not accessible", result);
      return result;
    }

    const typeOk = isPdfContentType(result.contentType);
    const blob = await response.blob();
    if (!result.contentLength) {
      result.contentLength = String(blob.size);
    }
    const magicOk = await looksLikePdfBlob(blob);

    if (typeOk === false || !magicOk) {
      result.ok = false;
      result.error = magicOk
        ? `unexpected content-type: ${result.contentType || "-"}`
        : "not a PDF (HTML/SPA fallback or empty file)";
      result.elapsedMs = Math.round(performance.now() - started);
      console.warn("[PDF] probe rejected fake/missing PDF", {
        ...result,
        bytes: blob.size,
        magicOk,
        typeOk,
      });
      return result;
    }

    result.ok = true;
    result.elapsedMs = Math.round(performance.now() - started);
    console.log(
      "[PDF] probe result:",
      `ok=${result.ok}`,
      `status=${result.status}`,
      `contentType=${result.contentType || "-"}`,
      `contentLength=${result.contentLength || "-"}`,
      `bytes=${blob.size}`,
      `elapsedMs=${result.elapsedMs}`,
      `url=${result.url}`
    );
    return result;
  } catch (err) {
    result.elapsedMs = Math.round(performance.now() - started);
    result.error = err?.message || String(err);
    console.error("[PDF] probe failed (network/CORS)", result, err);
    return result;
  }
}

/**
 * Download PDF and return a blob: URL for embedding.
 * Avoids X-Frame-Options / "refused to connect" when embedding same-site PDF.
 */
async function fetchPdfAsObjectUrl(url) {
  const started = performance.now();
  console.log("[PDF] fetch blob start", String(url));
  const response = await fetch(url, { method: "GET", cache: "no-store" });
  if (!response.ok) {
    throw new Error(`PDF fetch failed: ${response.status} ${response.statusText}`);
  }
  const blob = await response.blob();
  const magicOk = await looksLikePdfBlob(blob);
  if (!magicOk) {
    console.error("[PDF] fetch is not a real PDF", {
      bytes: blob.size,
      type: blob.type,
      url,
    });
    throw new Error("Downloaded file is not a PDF (server returned HTML/other)");
  }
  const pdfBlob =
    blob.type && blob.type.includes("pdf")
      ? blob
      : new Blob([blob], { type: "application/pdf" });
  const objectUrl = URL.createObjectURL(pdfBlob);
  console.log(
    "[PDF] fetch blob ok:",
    `bytes=${pdfBlob.size}`,
    `type=${pdfBlob.type || "-"}`,
    `ms=${Math.round(performance.now() - started)}`,
    `objectUrl=${objectUrl}`
  );
  return { objectUrl, size: pdfBlob.size, type: pdfBlob.type };
}

let baseURL = window.location.origin;

const weblink = process.env.NODE_ENV === "production"
  ? baseURL + "/"
  : "http://localhost:8080/";

// ateshgah
// progress-ims

/**
 * reCAPTCHA site keys by host:
 * - localhost → LOCAL
 * - *.ateshgah.com → ATESHGAH
 * - Progress / other → PROGRESS
 */
const RECAPTCHA_SITE_KEY_LOCAL = "6LcaCygqAAAAAN1cRBFziTp3m4ANLorzOKb4OkjN";
const RECAPTCHA_SITE_KEY_ATESHGAH = "6LfBLVMtAAAAALz7wgBzGSY0sTcpJepc9eQ24Ton";
const RECAPTCHA_SITE_KEY_PROGRESS = "6LfxD_0sAAAAAGaqsyqngcwb7qdcja1EDt53eKQC";
// Secret (backend): Progress/prev = 6LfxD_0sAAAAAH7ERjeVmzsuX2ivm3fm7F6BZ-r5 ; Ateshgah = 6LfBLVMtAAAAAMf_E-U9vKz7blndB8YLFNF3TYUn

function resolveRecaptchaSiteKey() {
  if (typeof window === "undefined") {
    return process.env.NODE_ENV === "production"
      ? RECAPTCHA_SITE_KEY_ATESHGAH
      : RECAPTCHA_SITE_KEY_LOCAL;
  }
  const host = String(window.location.hostname || "").toLowerCase();
  if (host === "localhost" || host === "127.0.0.1" || host === "[::1]") {
    return RECAPTCHA_SITE_KEY_LOCAL;
  }
  if (host.includes("ateshgah")) {
    return RECAPTCHA_SITE_KEY_ATESHGAH;
  }
  // progress-ims.com and any other Progress deploy
  return RECAPTCHA_SITE_KEY_PROGRESS;
}

const recaptchaKey = resolveRecaptchaSiteKey();

const RECAPTCHA_SCRIPT_ID = "recaptcha-script-explicit";
let recaptchaApiPromise = null;

/** Remove non-explicit api.js loads that race with vue3-recaptcha2 explicit render. */
function removeConflictingRecaptchaScripts() {
  if (typeof document === "undefined") return;
  document.querySelectorAll('script[src*="recaptcha/api.js"]').forEach((node) => {
    if (!node.src.includes("render=explicit")) {
      node.remove();
    }
  });
}

/**
 * Ensure Google reCAPTCHA api.js is loaded once (explicit render).
 * Safe to call before mounting vue3-recaptcha2.
 */
function ensureRecaptchaApi(hl = "") {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("recaptcha unavailable"));
  }

  if (window.grecaptcha?.ready) {
    return new Promise((resolve) => {
      window.grecaptcha.ready(resolve);
    });
  }

  if (recaptchaApiPromise) return recaptchaApiPromise;

  removeConflictingRecaptchaScripts();

  recaptchaApiPromise = new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn) => {
      if (settled) return;
      settled = true;
      fn();
    };

    const resolveWhenReady = () => {
      finish(() => {
        if (window.grecaptcha?.ready) {
          window.grecaptcha.ready(resolve);
        } else {
          resolve();
        }
      });
    };

    const existing = document.getElementById(RECAPTCHA_SCRIPT_ID);
    if (existing) {
      if (window.grecaptcha?.ready) {
        resolveWhenReady();
      } else {
        window.recaptchaReady = resolveWhenReady;
      }
      return;
    }

    window.recaptchaReady = resolveWhenReady;

    const script = document.createElement("script");
    script.id = RECAPTCHA_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    const lang = hl ? `&hl=${encodeURIComponent(hl)}` : "";
    script.src = `https://www.google.com/recaptcha/api.js?onload=recaptchaReady&render=explicit${lang}`;
    script.onerror = () => {
      recaptchaApiPromise = null;
      finish(() => reject(new Error("recaptcha load failed")));
    };
    document.head.appendChild(script);

    window.setTimeout(() => {
      if (settled || window.grecaptcha?.ready) return;
      recaptchaApiPromise = null;
      finish(() => reject(new Error("recaptcha timeout")));
    }, 20000);
  });

  return recaptchaApiPromise;
}

const fileurl = process.env.NODE_ENV === "production"
  ?
  "https://inspector-files.progress-ims.com/2024/"
  : "http://localhost:5002/";

const standalone = true;

let imgFile = 'Agroup'

const domain = window.location.host
var subdomain = domain.split('.')[0]
function setCssVariables() {
  const root = document.documentElement;

  if (subdomain.includes('medical-mega-atg')) {
    imgFile = 'Mega'
    root.style.setProperty('--bs-info', '#089742');
    root.style.setProperty('--bs-success', '#a80067');
    root.style.setProperty('--bs-warning', '#FF0080');
  } else if (subdomain.includes('medical-account-agroup')) {
    root.style.setProperty('--bs-info', '#a72923');
    root.style.setProperty('--bs-success', '#c82518');
    root.style.setProperty('--bs-warning', '#FF4F4B');
  } else {
    imgFile = 'Atg'
    root.style.setProperty('--bs-info', '#E83229');
    root.style.setProperty('--bs-success', '#F4982C');
    root.style.setProperty('--bs-warning', '#fbcf33');
  } 
}

setCssVariables();



function HtmlNewLine(value) {
  return value.replace(/(?:\r\n|\r|\n)/g, "<br />");
}

function Round2(num) {
  return Math.round(num * 100) / 100;
}

function DaysBetween(dt1, dt2) {
  var oneday = 1000 * 60 * 60 * 24;
  dt1 = new Date(dt1);
  dt2 = new Date(dt2);
  var diff = Math.round(dt2.getTime() - dt1.getTime()) / oneday;

  var res = diff.toFixed(0);
  return res;
}

function DateToString(dt) {
  dt = new Date(dt);
  let date = ("0" + dt.getDate()).slice(-2);

  let month = ("0" + (dt.getMonth() + 1)).slice(-2);

  let year = dt.getFullYear();

  return year + "-" + month + "-" + date;
}

function DateTimeToString(dt) {
  dt = new Date(dt);
  let date = ("0" + dt.getDate()).slice(-2);

  let month = ("0" + (dt.getMonth() + 1)).slice(-2);

  let year = dt.getFullYear();

  let hour = dt
    .getHours()
    .toString()
    .padStart(2, "0");

  let minutes = dt
    .getMinutes()
    .toString()
    .padStart(2, "0");

  let seconds = dt
    .getSeconds()
    .toString()
    .padStart(2, "0");

  return (
    year + "-" + month + "-" + date + " " + hour + ":" + minutes + ":" + seconds
  );
}

function DateTimeReverseToString(dt) {
  dt = new Date(dt);
  let date = ("0" + dt.getDate()).slice(-2);

  let month = ("0" + (dt.getMonth() + 1)).slice(-2);

  let year = dt.getFullYear();

  let hour = dt
    .getHours()
    .toString()
    .padStart(2, "0");

  let minutes = dt
    .getMinutes()
    .toString()
    .padStart(2, "0");

  let seconds = dt
    .getSeconds()
    .toString()
    .padStart(2, "0");
  return (
    date + "-" + month + "-" + year + " " + hour + ":" + minutes + ":" + seconds
  );
}


function DateTimeReverseToString1(dt) {

  dt = new Date(dt);
  let date = ("0" + dt.getDate()).slice(-2);

  let month = ("0" + (dt.getMonth() + 1)).slice(-2);

  let year = dt.getFullYear();

  let hour = dt
    .getHours()
    .toString()
    .padStart(2, "0");

  let minutes = dt
    .getMinutes()
    .toString()
    .padStart(2, "0");

  let seconds = dt
    .getSeconds()
    .toString()
    .padStart(2, "0");

  return (
    month + "-" + date + "-" + year + " " + hour + ":" + minutes + ":" + seconds
  );
}

function DateTimeToJson(dt) {
  dt = new Date(dt);
  let date = ("0" + dt.getDate()).slice(-2);

  let month = ("0" + (dt.getMonth() + 1)).slice(-2);

  let year = dt.getFullYear();

  let hour = dt
    .getHours()
    .toString()
    .padStart(2, "0");

  let minutes = dt
    .getMinutes()
    .toString()
    .padStart(2, "0");

  let seconds = dt
    .getSeconds()
    .toString()
    .padStart(2, "0");

  return (
    year + "-" + month + "-" + date + "T" + hour + ":" + minutes + ":" + seconds
  );
}

function AddMonths(date, months) {
  var d = date.getDate();
  date.setMonth(date.getMonth() + months);
  if (date.getDate() != d) {
    date.setDate(0);
  }
  return date;
}

function AddDays(date, days) {
  var result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}
function MonthName(month) {
  const monthNames = [
    "Yanvar",
    "Fevral",
    "Mart",
    "Aprel",
    "May",
    "İyun",
    "İyul",
    "Avqust",
    "Sentyabr",
    "Oktyabr",
    "Noyabr",
    "Dekabr"
  ];
  return monthNames[month];
}

function NumberFormat(val, digits) {
  return val.toLocaleString(
    "ru-RU", // leave undefined to use the visitor's browser
    // locale or a string like 'en-US' to override it.
    { minimumFractionDigits: digits }
  );
}

export {
  // ShowLink,
  HtmlNewLine,
  api,
  signalapi,
  printapi,
  buildPolicyPrintUrl,
  buildAgentRequestPrintUrl,
  buildPdfFileUrl,
  buildPrintPdfFileName,
  probePdfFileUrl,
  fetchPdfAsObjectUrl,
  PDF_FILES_FOLDER,
  PDF_PRINT_TYPE_POLICY,
  PDF_PRINT_TYPE_AGENT_REQUEST,
  smsapi,
  fileurl,
  standalone,
  weblink,
  Round2,
  recaptchaKey,
  ensureRecaptchaApi,
  DaysBetween,
  DateToString,
  DateTimeToString,
  AddMonths,
  AddDays,
  MonthName,
  NumberFormat,
  DateTimeToJson,
  DateTimeReverseToString,
  DateTimeReverseToString1,
  login,
  imgFile
};
